import React, {useEffect, useRef, useState} from 'react';
import {Button, Col, Input, notification, PageHeader, Row, Table} from 'antd';
import {ReloadOutlined, SearchOutlined, SyncOutlined, DownloadOutlined} from '@ant-design/icons';
import _ from 'lodash';
import {getPto, syncPto} from '../api/ptoApi';
import {useUser} from '../auth/userHooks';
import {AccrualSchedule} from './AccrualSchedule';
import moment from 'moment';
import {getPaginationConfig} from "../util/showAllPagination";
import {minutesToHours, PREFERRED_DATE_FORMAT, PREFERRED_DATETIME_FORMAT} from '../util/time';
import {useDynamicTableHeight} from "../util/useDynamicTableHeight";
import {Parser} from "json2csv";


const TimeOffTable = () => {
  const user = useUser();
  const [ptoRecords, setPtoRecords] = useState([]);
  const [filteredPtoRecords, setFilteredPtoRecords] = useState([]);
  const [syncing, setSyncing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showAccrualSchedule, setShowAccrualSchedule] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [searchText, setSearchText] = useState('');
  const tableContainerRef = useRef(null);
  const tableHeight = useDynamicTableHeight(tableContainerRef);

  const exportAccruals = () => {
    const csvData = filteredPtoRecords
      .map(a => {
        return {
          Name: a.employee.fullName,
          Email: a.employee.email,
          'Current Balance': minutesToHours(a.ptoBalance),
          'Last Updated At': moment(a.UpdatedAt).format(PREFERRED_DATETIME_FORMAT),
          'Previous Balance': minutesToHours(a.prevBalance),
          'Previous Updated At': a.prevBalanceUpdatedAt ? moment(a.prevBalanceUpdatedAt).format(PREFERRED_DATETIME_FORMAT) : '-',
          'PTO Policy': a.employee.ptoPolicy || '-',
          'Weekly Hours': a.weeklyHours,
          'Start Date': moment(a.employee.startDate).format(PREFERRED_DATE_FORMAT),
          Level: a.employee.level,
          'Current Accrual (hrs/month)': minutesToHours(a.currentAccrual)
        }
      });
    const json2CsvParser = new Parser();
    const csv = json2CsvParser.parse(csvData);
    const blob = new Blob([csv], {type: 'text/csv', encoding: 'UTF-8'});
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `PaidTimeOffExport-${moment().format('YYYYMMDD-hhmmss')}.csv`;
    link.click();
  }


  const columns = [
    {
      title: 'Name',
      width: 170,
      align: 'left',
      sorter: (a, b) => a.employee.fullName.localeCompare(b.employee.fullName),
      render: record => {
        if (!record.employee.primaryPsaInstance || record.employee.primaryPsaInstance.length === 0) {
          return record.employee.fullName;
        }
        return (
          <Button
            type="link"
            style={{paddingLeft: 0, paddingRight: 0}}
            onClick={() => loadAccrualSchedule(record)}>
            {record.employee.fullName}
          </Button>
        );
      }
    },
    {
      title: 'Email',
      defaultSortOrder: 'ascend',
      width: 160,
      sorter: (a, b) => a.employee.email.localeCompare(b.employee.email),
      render: record => record.employee.email
    },
    {
      title: 'Current Balance',
      width: 80,
      dataIndex: 'ptoBalance',
      align: 'left',
      render: ptoBalance => `${minutesToHours(ptoBalance)}`
    },
    {
      title: 'Last Updated At',
      align: 'left',
      width: 150,
      render: record => moment(record.UpdatedAt).format(PREFERRED_DATETIME_FORMAT)
    },
    {
      title: 'Previous Balance',
      dataIndex: 'prevBalance',
      width: 80,
      align: 'left',
      render: prevBalance => `${minutesToHours(prevBalance)}`
    },
    {
      title: 'Previous Updated At',
      width: 170,
      render: record => record.prevBalanceUpdatedAt ? moment(record.prevBalanceUpdatedAt).format(PREFERRED_DATETIME_FORMAT) : '-'
    },
    {
      title: 'PTO Policy',
      width: 110,
      render: (record) => {
        const policy = record.employee.ptoPolicy ? record.employee.ptoPolicy : null;
        const modifier = record.employee.ptoModifier ? record.employee.ptoModifier : null;
        const hoursOverride = record.employee.ptoHoursOverride ? record.employee.ptoHoursOverride : null;

        if (!policy && !modifier && !hoursOverride) return "-";

        let displayText = policy || "-";

        if (modifier) {
          displayText += ` (${modifier})`;
        }

        if (hoursOverride) {
          displayText += ` [Override: ${hoursOverride}h]`;
        }

        return displayText;
      },
    },
    {
      title: 'Weekly Hours',
      align: 'left',
      width: 70,
      dataIndex: 'weeklyHours'
    },
    {
      title: 'Start Date',
      width: 90,
      render: record => moment.utc(record.employee.startDate).format(PREFERRED_DATE_FORMAT)
    },
    {
      title: 'Level',
      align: 'left',
      width: 50,
      render: record => record.employee.level
    },{
      title: 'Current Accrual (hrs/month)',
      align: 'left',
      width: 80,
      dataIndex: 'currentAccrual',
      render: currentAccrual => `${minutesToHours(currentAccrual)}`
    },

  ];

  const load = async () => {
    try {
      setLoading(true);
      const records = await getPto();
      setPtoRecords(records);
      filterPtoRecords(records, searchText);
    } catch (e) {
      console.error(`Could not get pto data: ${e}`);
    } finally {
      setLoading(false);
    }
  };

  const loadAccrualSchedule = record => {
    setSelectedEmployee(record);
    setShowAccrualSchedule(true);
  };

  const hideAccrualSchedule = () => {
    setSelectedEmployee(null);
    setShowAccrualSchedule(false);
  };

  const sync = async () => {
    try {
      setSyncing(true);
      const {status} = await syncPto();
      if (status === 200) {
        notification.success({
          message: 'Success',
          description: `PTO balances and accruals are now being synchronized with Projector!`,
          placement: 'bottomRight',
        });
      } else {
        notification.warning({
          message: 'Error',
          description: `PTO balances and accruals could not be synchronized with Projector. Please contact na_opsenablement@credera.com`,
          placement: 'bottomRight',
        });
      }
    } catch (e) {
      console.error(`Could not sync PTO: `, e);
    } finally {
      setSyncing(false);
    }
  };

  const filterPtoRecords = (records, searchText) => {
    setFilteredPtoRecords(_.chain(records)
      .filter(u => searchText === '' || _.includes(_.toLower(u.employee.fullName), _.toLower(searchText)))
      .value());
  };

  const search = text => {
    setSearchText(text);
    filterPtoRecords(ptoRecords, text);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, []);

  return (
    <>
      <Row>
        <Col span={12}>
          <PageHeader title="Paid Time Off" subTitle={`(${filteredPtoRecords.length} shown)`} style={{padding: 0}}
          />
        </Col>
      </Row>

      <Row style={{marginBottom: 10}}>
        <Col span={6} style={{marginRight: 10}}>
          <Input placeholder="Search for employee(s) by name" prefix={<SearchOutlined/>}
                 onChange={e => search(e.target.value)} allowClear/>
        </Col>
        <Col flex="auto">
          <Button style={{float: 'right'}} onClick={() => load()} loading={loading} icon={<ReloadOutlined/>}>
            Refresh
          </Button>
          {user?.permissions?.canManagePto && (
            <Button style={{float: 'right', marginRight: 8}} onClick={() => sync()} loading={syncing}
                    icon={<SyncOutlined/>}>
              Synchronize PTO
            </Button>
          )}
          <Button style={{float: 'right', marginRight: 8}} onClick={exportAccruals} disabled={loading}
                  icon={<DownloadOutlined/>}>
            Export
          </Button>
        </Col>
      </Row>

      <Table
        ref={tableContainerRef}
        rowKey={record => record.employee.email}
        dataSource={filteredPtoRecords}
        columns={columns}
        pagination={getPaginationConfig(filteredPtoRecords.length)}
        size="small"
        loading={loading}
        scroll={{y: tableHeight}}
      />
      {selectedEmployee &&
        <AccrualSchedule
          employee={selectedEmployee}
          showAccrualSchedule={showAccrualSchedule}
          hideAccrualSchedule={hideAccrualSchedule}
        /> }

    </>
  );
};

export {TimeOffTable};